
/**
 * 		Application models for EVA website
 *
 * 		@author Irina Patrikeeva <irina.i.ipatrikeeva@nasa.gov>
 *
 *     	Created in 2015
 *     	Modified on 07/18/2018
 */

/*jshint esnext: true */
/*jshint strict:false */
/*globals Backbone */

/*** Global data structure for field types in the form <key>:<label> ***/
const FieldTypes = {
	number: 'number',
	enumeration: 'enumeration',
	largeEnumeration: 'largeEnumeration',
	string: 'string',
	date: 'date',
	object: 'object',
	boolean: 'boolean'
};

/*** Global data structure for field predicates in the form <key>:<value> ***/
const Predicates = {
	equals: 'eq',
	notEqual: 'ne',
	contains: 'cn',
	notContain: 'nc',
	lessThan: 'lt',
	greaterThan: 'gt',
	between: 'between'
};

/**
 * Global date filter values
 */
const DateFilter = {
    all: "Any time",
    today: "Today",
    yesterday: "Yesterday",
    week: "Last 7 days",
    month: "Last 30 days",
    ninetyDays: "Last 90 days",
    sixMonths: "Last 6 months",
    year: "Last year"
};

/**
 * Global date filter keys
 */
const DateFilterKeys = {
    all: "all",
    today: "today",
    yesterday: "yesterday",
    week: "week",
    month: "month",
    ninetyDays: "ninetyDays",
    sixMonths: "sixMonths",
    year: "year"
};

/*** Data model for a single field predicate ***/
var FieldPredicateModel = Backbone.Model.extend({
	defaults: {
		value: "",
		label: "",
		url: ""
	}
});

/*** Data collection of all possible field predicate models ***/
var FieldPredicateCollection = Backbone.Collection.extend({
	model: FieldPredicateModel,
	initialize: function() {
		this.add({ value: Predicates.equals, label: "Equals", url: "eq" });
		this.add({ value: Predicates.notEqual, label: "Doesn\'t Equal", url: "ne" });
		this.add({ value: Predicates.contains, label: "Contains", url: "cn" });
		this.add({ value: Predicates.notContain, label: "Doesn\'t Contain", url: "nc" });
		this.add({ value: Predicates.lessThan, label: "Less than", url: "lt" });
		this.add({ value: Predicates.greaterThan, label: "Greater than", url: "gt" });
		this.add({ value: Predicates.between, label: "Between", url: "between" });
	}
});

/*** Data model for a single record ***/
var RecordModel = Backbone.Model.extend({
	defaults: {
		ACL_ALLOW: "",
		id: "",
		type: "",
		title: "",
		url: "",
		indexed: null
	}
});

/*** Data collection of record models ***/
var SearchResultsCollection = Backbone.Collection.extend({
	model: RecordModel
});

/*** Data models with labels & types ***/

/*** FieldModel is the object representing one field in a record, such as title ***/
var FieldModel = Backbone.Model.extend({
	defaults: {
		name: "",
		label: "",
		value: "",
		type: null
	}
});

/*** RecordCollection is the object representing one record. It is a Backbone Collection object that has models for each field) ***/
var RecordCollection = Backbone.Collection.extend({
	model: FieldModel
});

/*** GenericRecordCollection is a Backbone Collection object that represents a generic (typeless) record with default fields, such as title, url, etc. ***/
var GenericRecordCollection = RecordCollection.extend({
	initialize: function() {
		this.add({ name: "ACL_ALLOW", label: "ACL_ALLOW", type: FieldTypes.enumeration, value: "" });
		this.add({ name: "ACL_DENY", label: "ACL_DENY", type: FieldTypes.enumeration, value: "" });
		this.add({ name: "id", label: "ID", type: FieldTypes.enumeration, value: "" });
		this.add({ name: "title", label: "Title", type: FieldTypes.string, value: "" });
		this.add({ name: "url", label: "URL", type: FieldTypes.enumeration, value: "" });
		this.add({ name: "type", label: "Type", type: FieldTypes.string, value: "" });
		this.add({ name: "indexed", label: "Indexed", type: FieldTypes.date, value: null });
	},
	isRelatedField: function(field) {
		return this.relatableFields && this.relatableFields.indexOf(field) !== -1;
	}
});

/*** EventCollection is a Backbone Collection object that represents an Event record for Hardware Data ***/
var EventCollection = GenericRecordCollection.extend({
	initialize: function() {
		EventCollection.__super__.initialize.apply(this, arguments);
	}
});

/*** Backbone Collection that represents a single document ***/
var DocumentCollection = GenericRecordCollection.extend({
	initialize: function () {
		DocumentCollection.__super__.initialize.apply(this, arguments);
	}
});


/*** EventModel is a Backbone Model that represents an Event record for Hardware Data ***/
var EventModel = Backbone.Model.extend({
    defaults: {
        id: "",
        title: "",
        description: "",
        status: "",
        eventType: "",
        lastUpdatedDate: null,
        entryDate: null,
        eventDate: null
    }
});


/*** ResultsSummaryModel is a Backbone Model that represents a summary of search results
 * @took is the time in milliseconds
 * @total is the number of records
 * ***/
var ResultsSummaryModel = Backbone.Model.extend({
	defaults: {
		took: 0,
		total: "",
		query: ""
	}
});

/*** SearchErrorModel is a Backbone Model that represents an error message for failed search query
 * @url is the search url
 * @query is the exectured query
 * @status is the error status (404, 403, etc.)
 * @statusText is the status message
 */
var SearchErrorModel = Backbone.Model.extend({
	defaults: {
		url: "",
		query: "",
		status: "",
		statusText: ""
	}
});

/*** PaginationModel is a Backbone Model that represents pagination controls
 * @start is the starting index (number)
 * @end is the end index of the current page (number)
 * @nPages is the total number of pages found
 * @currentPage is the current page of results shown to the user
 * ***/
var PaginationModel = Backbone.Model.extend({
	defaults: {
		start: 0,
		end: 19,
		nPages: 0,
		currentPage: 1,
		type: '',
		dataType: ''
	}
});

/*** FilterModel is a Backbone Model that represents a search filter / facet
 * @field is field name, e.g. cf_documenttype
 * @label is field display name, e.g. Associated Document Type
 * @value is field value, e.g. "
 * @displayValue is field display value, e.g. FIAR
 * @operator is field predicate, e.g. 'eq'
 * @type is type of the filter, one of the FieldTypes
 * ***/
var FilterModel = Backbone.Model.extend({
	defaults: {
		field: "",
		label: "",
		value: "",
		displayValue: "",
		operator: "",
		type: ""
	}
});

/*** FilterCollection is a Backbone Collection of FilterModels ***/
var FilterCollection = Backbone.Collection.extend({
	model: FilterModel
});

/*** FiltersModel is a Backbone Model that represents a group of filters in one category
 * @param name {string} is filter category name, e.g. cf_program
 * @param label {string} is display filter category name
 * @param originalName {string} source name of the filter
 * @param type {string} data type of filter
 * ***/
var FiltersModel = Backbone.Model.extend({
	defaults: {
		name: "",
		label: "",
		originalName: "",
		type: ""
	}
});

/*** ChartModel is a Backbone Model that represents one data chart
 * @title is chart title, e.g. 'Fabrication Action Breakdown'
 * @id is chart element id, e.g. 'fab-chart',
 * @data is data to graph
 * ***/
var ChartModel = Backbone.Model.extend({
	defaults: {
		title: "",
		id: "",
		data: ""
	}
});

/*** ChartCollection is a Backbone Collection of ChartModels ***/
var ChartCollection = Backbone.Collection.extend({
	model: ChartModel
});

/*** ValueModel is a Backbone Model that represents an object
 * @value is object value
 * @hidden is boolean; if false it won't be displayed
 * ***/
var ValueModel = Backbone.Model.extend({
	defaults: {
		value: "",
		hidden: ""
	}
});

/*** UserModel is a Backbone Model that represents current user
 * @name is user name
 * @groups is user permission groups
 * ***/
var UserModel = Backbone.Model.extend({
	defaults: {
		name: "",
		id: "",
		groups: ""
	}
});

/*** DocumentsSummaryModel is a Backbone Model that represents summary status of event documents
 * @status is open or closed
 * @total is number of documents
 * @type is document type
 * ***/
var DocumentsSummaryModel = Backbone.Model.extend({
	defaults: {
		status: '',
		total: "",
		type: ''
	}
});
/*** KeyValueModel is a Backbone Model that represents an object, such as a record field row in a single record view
 * @key is object key, e.g. field display name "Project"
 * @value is object value, e.g. field display value "EVA GFE EMU"
 * @hidden is boolean; if false it won't be displayed
 * @relatedUrl is URL based on @value, e.g. Part Number
 * ***/
var KeyValueModel = Backbone.Model.extend({
    defaults: {
        key: "",
        value: "",
        hidden: "",
        relatedUrl: ""
    }
});